import type { TimestampMillis } from './common';

export const UPLOAD_ALLOWED_EXTENSIONS = ['lua', 'luau'] as const;

export type UploadAllowedExtension = (typeof UPLOAD_ALLOWED_EXTENSIONS)[number];

export const UPLOAD_REJECTION_REASONS = [
  'file_name_invalid',
  'extension_unsupported',
  'file_empty',
  'file_too_large',
  'binary_signature',
  'compiled_bytecode',
  'encoding_invalid',
  'control_characters',
  'not_multipart',
  'missing_file_field',
  'unexpected_field',
  'storage_unavailable',
] as const;

export type UploadRejectionReason = (typeof UPLOAD_REJECTION_REASONS)[number];

export const SCRIPT_UPLOAD_LIMITS = {
  minBytes: 1,
  maxBytes: 2 * 1024 * 1024,
  maxFileNameLength: 180,
  signatureSampleBytes: 8192,
  controlSampleBytes: 262_144,
  retentionMs: 86_400_000,
  sweepBatchSize: 24,
} as const;

export const SCRIPT_UPLOAD_FILE_FIELD = 'source';

export type ScriptUploadStatus = 'stored' | 'discarded';

export interface ScriptUpload {
  id: string;
  filename: string;
  extension: UploadAllowedExtension;
  size_bytes: number;
  checksum_sha256: string;
  status: ScriptUploadStatus;
  created_at: TimestampMillis;
  expires_at: TimestampMillis;
}

export interface ScriptUploadResponse {
  upload: ScriptUpload;
  duplicate: boolean;
}

export interface DiscardScriptUploadResponse {
  id: string;
  removed: boolean;
}

export interface UploadRejection {
  reason: UploadRejectionReason;
  field: string;
  limit_bytes?: number | undefined;
  found_bytes?: number | undefined;
  extension?: string | undefined;
}

export interface UploadInspectionInput {
  fileName: string;
  sizeBytes: number;
  sample: Uint8Array;
  complete: boolean;
}

export type UploadInspection = { ok: true } | { ok: false; rejection: UploadRejection };

export function isUploadAllowedExtension(value: unknown): value is UploadAllowedExtension {
  return (
    typeof value === 'string' &&
    (UPLOAD_ALLOWED_EXTENSIONS as readonly string[]).includes(value.toLowerCase())
  );
}

export function isUploadRejectionReason(value: unknown): value is UploadRejectionReason {
  const list: readonly string[] = UPLOAD_REJECTION_REASONS;
  return typeof value === 'string' && list.includes(value);
}

export const SCRIPT_UPLOAD_STATUSES = ['stored', 'discarded'] as const;

export function isScriptUploadStatus(value: unknown): value is ScriptUploadStatus {
  const list: readonly string[] = SCRIPT_UPLOAD_STATUSES;
  return typeof value === 'string' && list.includes(value);
}

export const UPLOAD_PATH_SEPARATORS = ['/', '\\'] as const;

export function uploadLeafOf(fileName: string): string {
  let leaf = fileName;
  for (const separator of UPLOAD_PATH_SEPARATORS) {
    const index = leaf.lastIndexOf(separator);
    if (index >= 0) {
      leaf = leaf.slice(index + 1);
    }
  }
  return leaf;
}

export function uploadExtensionOf(fileName: string): string {
  const leaf = uploadLeafOf(fileName);
  const separator = leaf.lastIndexOf('.');
  if (separator <= 0 || separator === leaf.length - 1) {
    return '';
  }
  return leaf.slice(separator + 1).toLowerCase();
}

export function isOverUploadLimit(
  sizeBytes: number,
  maxBytes: number = SCRIPT_UPLOAD_LIMITS.maxBytes,
): boolean {
  return !Number.isSafeInteger(sizeBytes) || sizeBytes > maxBytes;
}
